import React from 'react';
import { TTransactionHistory } from '../../types/TransactionHistory';
import {
  AttachmentNotProvidedIcon,
  PlusFilledIcon,
  AttachmentCheckedIcon
} from '@/components/Icons';
import { IPurchaseTransaction, TransactionTypeEnum } from '@/services/types/TransactionData';
import { TCustomer } from '@/context/types/Customer';
import Tooltip from '@/components/Tooltip';
import { getHumanReadableReceiptExemption } from '../../utils/getHumanReadableReceiptExemption';
import { isAttachmentRequired } from '../../utils/isReceiptRequired';

interface ReceiptCellProps {
  customer: TCustomer | undefined;
  transaction: TTransactionHistory;
}

const ReceiptCell: React.FC<ReceiptCellProps> = ({ transaction }) => {
  if (transaction.type == TransactionTypeEnum.Purchase) {
    const data = transaction.data as IPurchaseTransaction;

    const hasAttachments = data.hasAttachments;

    if (isAttachmentRequired(data)) {
      // Show the 'receipt required indicator' if no attachment is provided and no receipt exemption reason is provided
      // Business owner and admin can add a receipt exemption reason here even if they don't own the transaction.
      return (
        <Tooltip content="A receipt is required for this transaction" translateY="-0.75rem" animate>
          <div className="h-[30px] w-[30px] rounded-full bg-emerge-purple-3 flex items-center justify-center">
            <PlusFilledIcon color="purple" size="xs" />
          </div>
        </Tooltip>
      );
    } else if (hasAttachments) {
      return <AttachmentCheckedIcon size="md" />;
    } else if (data.receiptExemptionReason != null) {
      // Show the 'no receipt indicator' if receipt exemption reason is provided
      return (
        <Tooltip
          content={`No receipt: ${getHumanReadableReceiptExemption(data.receiptExemptionReason)}`}
          translateY="-0.75rem">
          <AttachmentNotProvidedIcon size="md" />
        </Tooltip>
      );
    } else if (!hasAttachments) {
      // Show the 'add receipt indicator' if they own the transaction
      return (
        <div className="h-[30px] w-[30px] rounded-full bg-emerge-teal-5 flex items-center justify-center">
          <PlusFilledIcon color="navy" size="xs" />
        </div>
      );
    }
  }

  return transaction?.details ? null : (
    <div className="animate-pulse h-9 w-6 rounded-lg bg-aux-wizardwhite" />
  );
};

export default ReceiptCell;
